import React, { useCallback, useEffect, useRef, useState } from "react";
import { CloseOutlined, HistoryOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { apiConfig } from "../authConfig";
import { ActivityLogTimeline } from "./ActivityLogTimeline";
import { formatRelativeTime, getActionLabel, getRoleBadge } from "./activityLogConstants";
import { formatDdMmYyyy } from "../utils/formatDdMmYyyy";

/**
 * Collapsible activity log drawer — renders at the bottom-right of any workspace section.
 * Props: projectId, section (optional filter), getApiToken
 */
export function ActivityLogDrawer({ projectId, section, getApiToken }) {
  const [expanded, setExpanded] = useState(false);
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [filterAll, setFilterAll] = useState(false);
  const fetchedRef = useRef(false);

  const fetchEvents = useCallback(
    async (skip = 0, append = false) => {
      if (!projectId) return;
      const isMore = skip > 0;
      if (isMore) setLoadingMore(true);
      else setLoading(true);

      try {
        const token = await getApiToken();
        const params = new URLSearchParams({ limit: "20", skip: String(skip) });
        if (section && !filterAll) params.set("section", section);
        const res = await fetch(
          `${apiConfig.baseUrl}/api/v1/projects/${projectId}/activity-log?${params}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const json = await res.json().catch(() => ({}));
        if (res.ok && json.success) {
          const newEvents = json.data?.events || [];
          setEvents((prev) => (append ? [...prev, ...newEvents] : newEvents));
          setTotal(json.meta?.total ?? newEvents.length);
        }
      } catch {
        /* best-effort */
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    },
    [projectId, section, getApiToken, filterAll]
  );

  useEffect(() => {
    if (!fetchedRef.current && projectId) {
      fetchedRef.current = true;
      fetchEvents(0);
    }
  }, [fetchEvents, projectId]);

  useEffect(() => {
    if (fetchedRef.current) fetchEvents(0);
  }, [filterAll]);

  const handleLoadMore = () => {
    fetchEvents(events.length, true);
  };

  const recentPreview = events.slice(0, 3);
  const todayCount = events.filter(
    (e) => formatDdMmYyyy(e.timestamp) === formatDdMmYyyy(Date.now())
  ).length;

  if (!projectId) return null;

  if (expanded) {
    return (
      <div className="fixed right-0 top-0 z-[900] flex h-full w-[400px] flex-col border-l border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] shadow-2xl animate-in slide-in-from-right duration-300">
        <div className="flex items-center justify-between border-b border-[color:var(--color-border)] px-5 py-3">
          <div className="flex items-center gap-2">
            <HistoryOutlined className="text-[color:var(--color-accent)]" />
            <span className="text-sm font-black text-[color:var(--color-text-hero)]">Activity Log</span>
            {total > 0 ? (
              <span className="rounded-full bg-[color:var(--color-bg-alt)] px-2 py-0.5 text-[10px] font-bold text-[color:var(--color-text-muted)]">
                {total}
              </span>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="flex h-7 w-7 items-center justify-center rounded-lg text-[color:var(--color-text-muted)] hover:bg-[color:var(--color-bg-alt)] hover:text-[color:var(--color-text-hero)]"
          >
            <CloseOutlined className="text-xs" />
          </button>
        </div>

        {section ? (
          <div className="flex border-b border-[color:var(--color-border)] px-5 py-2">
            <button
              type="button"
              onClick={() => setFilterAll(false)}
              className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${
                !filterAll ? "bg-[color:var(--color-accent-fill)] text-white" : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)]"
              }`}
            >
              This section
            </button>
            <button
              type="button"
              onClick={() => setFilterAll(true)}
              className={`rounded-full px-3 py-1 text-[11px] font-bold transition-all ${
                filterAll ? "bg-[color:var(--color-accent-fill)] text-white" : "text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text-hero)]"
              }`}
            >
              All project activity
            </button>
          </div>
        ) : null}

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <ActivityLogTimeline events={events} loading={loading} />
          {events.length < total ? (
            <div className="mt-4 text-center">
              <Button size="small" loading={loadingMore} onClick={handleLoadMore}>
                Load more
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed bottom-5 right-5 z-[800] animate-in fade-in slide-in-from-bottom-4 duration-500">
      <button
        type="button"
        onClick={() => {
          setExpanded(true);
          if (events.length === 0) fetchEvents(0);
        }}
        className="group flex flex-col items-end gap-1.5 rounded-2xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-white)] px-4 py-3 shadow-lg backdrop-blur-sm transition-all hover:shadow-xl hover:border-[color:var(--color-info-line)]"
      >
        <div className="flex items-center gap-2">
          <HistoryOutlined className="text-[color:var(--color-accent)] text-sm" />
          <span className="text-[11px] font-black uppercase tracking-widest text-[color:var(--color-text-muted)]">
            Activity
          </span>
          {todayCount > 0 ? (
            <span className="rounded-full bg-[color:var(--color-info-bg)] px-1.5 py-0.5 text-[9px] font-black text-[color:var(--color-accent)]">
              {todayCount} today
            </span>
          ) : null}
        </div>
        {recentPreview.length > 0 ? (
          <div className="w-full space-y-1 border-t border-[color:var(--color-border)] pt-2">
            {recentPreview.map((ev) => {
              const badge = getRoleBadge(ev.actor?.role);
              return (
                <div key={ev._id || ev.timestamp} className="flex items-center gap-1.5 text-[10px]">
                  <span className={`inline-flex h-3.5 w-3.5 items-center justify-center rounded-full text-[6px] font-black ${badge.color}`}>
                    {badge.label}
                  </span>
                  <span className="truncate text-[color:var(--color-text-muted)]">{getActionLabel(ev.action)}</span>
                  <span className="shrink-0 text-[color:var(--color-text-muted)]">{formatRelativeTime(ev.timestamp)}</span>
                </div>
              );
            })}
          </div>
        ) : loading ? (
          <span className="text-[10px] text-[color:var(--color-text-muted)]">Loading…</span>
        ) : (
          <span className="text-[10px] text-[color:var(--color-text-muted)]">No recent activity</span>
        )}
      </button>
    </div>
  );
}
